import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Globe, 
  CheckCircle, 
  Clock, 
  XCircle,
  RefreshCw,
  Search,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Link2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

interface WebsiteData {
  id: string;
  domain: string;
  title: string;
  favicon_url: string;
}

interface GoogleIndexTabProps {
  website: WebsiteData;
  isActive?: boolean;
}

interface IndexData {
  website_id: string;
  url: string;
  status: 'indexed' | 'pending' | 'error' | 'not_indexed';
  last_crawled: string;
  impressions: number;
  clicks: number;
  ctr: number;
  avg_position: number;
  crawl_errors: number;
  sitemap_submitted: boolean;
  data_source: string;
}

interface IndexSummary {
  totalPages: number;
  indexedPages: number;
  notIndexedPages: number;
  crawlErrors: number;
  sitemapSubmitted: boolean;
}

type SortField = 'url' | 'status' | 'last_crawled' | 'impressions' | 'clicks' | 'avg_position';
type SortDirection = 'asc' | 'desc';

export function GoogleIndexTab({ website, isActive = true }: GoogleIndexTabProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [indexData, setIndexData] = useState<IndexData[]>([]);
  const [summary, setSummary] = useState<IndexSummary | null>(null);
  const [isSimulated, setIsSimulated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnectedToGoogle, setIsConnectedToGoogle] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<SortField>('last_crawled');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Track if data has been loaded initially for current website
  const [loadedWebsiteId, setLoadedWebsiteId] = useState<string | null>(null);

  const connectToGoogle = () => {
    navigate('/settings');
  };

  useEffect(() => {
    // Only load data if tab is active
    if (!isActive) return;
    
    // Reset loaded state if website changed
    if (website?.id !== loadedWebsiteId) {
      setLoadedWebsiteId(null);
    }
    
    if (website?.id && website.id !== loadedWebsiteId) {
      loadIndexData();
      setLoadedWebsiteId(website.id);
    }
  }, [website?.id, isActive, loadedWebsiteId]);

  // Reload data when tab becomes active (in case connection was established while on another tab)
  useEffect(() => {
    if (isActive && website?.id && !loading) {
      const reloadIfConnected = async () => {
        const connected = await checkGoogleConnection();
        // If connected but no data, try to load
        if (connected && indexData.length === 0) {
          loadIndexData();
        }
      };
      
      // Small delay to allow other effects to complete
      const timeout = setTimeout(reloadIfConnected, 500);
      return () => clearTimeout(timeout);
    }
  }, [isActive]);

  // Sorting logic
  const sortedData = [...indexData].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];
    
    // Handle different data types
    if (sortField === 'last_crawled') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    } else if (sortField === 'status') {
      // Custom status ordering: indexed > pending > not_indexed > error
      const statusOrder = { 'indexed': 4, 'pending': 3, 'not_indexed': 2, 'error': 1 };
      aValue = statusOrder[aValue as keyof typeof statusOrder] || 0;
      bValue = statusOrder[bValue as keyof typeof statusOrder] || 0;
    } else if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = sortedData.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSortChange = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const checkGoogleConnection = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        setIsConnectedToGoogle(false);
        return false;
      }

      const { data: settingsData } = await supabase
        .from('user_settings')
        .select('google_search_console_access_token')
        .eq('user_id', user.id)
        .single();

      const connected = !!settingsData?.google_search_console_access_token;
      setIsConnectedToGoogle(connected);
      return connected;
    } catch (error) {
      console.error('Error checking Google connection:', error);
      setIsConnectedToGoogle(false);
      return false;
    }
  };

  const loadIndexData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check Google Search Console connection first
      const isConnected = await checkGoogleConnection();
      
      // If not connected, don't try to load data
      if (!isConnected) {
        setLoading(false);
        return;
      }

      // First try to get existing data from database
      const { data: existingData, error: dbError } = await supabase
        .from('google_index_data')
        .select('*')
        .eq('website_id', website.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (!dbError && existingData && existingData.length > 0) {
        setIndexData(existingData);
        
        // Calculate summary from existing data
        const totalPages = existingData.length;
        const indexedPages = existingData.filter(page => page.status === 'indexed').length;
        const notIndexedPages = existingData.filter(page => page.status === 'not_indexed').length;
        const crawlErrors = existingData.filter(page => page.status === 'error').length;
        
        setSummary({
          totalPages,
          indexedPages,
          notIndexedPages,
          crawlErrors,
          sitemapSubmitted: existingData[0]?.sitemap_submitted || false
        });
        
        // Only set simulated if data exists and is actually simulated
        setIsSimulated(existingData.length > 0 && existingData[0]?.data_source !== 'google_search_console_real');
        setLoading(false);
        return;
      }

      // If no existing data, fetch from edge function
      const { data, error } = await supabase.functions.invoke('fetch-google-index-data', {
        body: {
          websiteId: website.id,
          domain: website.domain
        }
      });

      if (error) {
        console.error('Error fetching Google Index data:', error);
        // Check if it's a connection error
        if (error.message?.includes('not connected') || error.message?.includes('No authorization')) {
          setIsConnectedToGoogle(false);
        } else {
          setError('Failed to fetch Google Index data. Please try again.');
        }
        setLoading(false);
        return;
      }

      if (data && data.success) {
        setIndexData(data.data || []);
        setSummary(data.summary);
        // Only set simulated if we have data AND it's marked as simulated
        // If no data but connected, don't show simulated message
        setIsSimulated(data.data && data.data.length > 0 && (data.isSimulated || false));
      } else if (data && !data.success) {
        // Check if it's a connection error
        if (data.error?.includes('not connected') || data.error?.includes('No authorization')) {
          setIsConnectedToGoogle(false);
        } else {
          logger.error('Failed to fetch Google Index data:', data.error);
          setError(data.error || 'Failed to fetch Google Index data');
        }
      }
    } catch (err) {
      logger.error('Error loading Google Index data:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkIndexStatus = async () => {
    setChecking(true);
    setError(null);
    
    try {
      // Check connection first
      const isConnected = await checkGoogleConnection();
      if (!isConnected) {
        setChecking(false);
        return;
      }

      // Clear existing data first
      setIndexData([]);
      setSummary(null);
      
      const { data, error } = await supabase.functions.invoke('fetch-google-index-data', {
        body: {
          websiteId: website.id,
          domain: website.domain
        }
      });

      if (error) {
        console.error('Error refreshing Google Index data:', error);
        // Check if it's a connection error
        if (error.message?.includes('not connected') || error.message?.includes('No authorization')) {
          setIsConnectedToGoogle(false);
        } else {
          setError(error.message || 'Failed to refresh Google Index data. Please try again.');
        }
        setChecking(false);
        return;
      }

      if (data && data.success) {
        setIndexData(data.data || []);
        setSummary(data.summary);
        // Only set simulated if we have data AND it's marked as simulated
        setIsSimulated(data.data && data.data.length > 0 && (data.isSimulated || false));
        setError(null);
      } else {
        const errorMsg = data?.error || 'Failed to refresh Google Index data';
        // Check if it's a connection error
        if (errorMsg.includes('not connected') || errorMsg.includes('No authorization')) {
          setIsConnectedToGoogle(false);
        } else {
          logger.error('Failed to refresh Google Index data:', errorMsg);
          setError(errorMsg);
        }
      }
    } catch (err: any) {
      logger.error('Error refreshing Google Index data:', err);
      setError(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setChecking(false);
    }
  };


  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'indexed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Globe className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'indexed':
        return <Badge variant="default" className="text-xs rounded-full px-3 py-1 font-semibold">Indexed</Badge>;
      case 'pending':
        return <Badge variant="secondary" className="text-xs rounded-full px-3 py-1 font-semibold">Pending</Badge>;
      case 'error':
        return <Badge variant="destructive" className="text-xs rounded-full px-3 py-1 font-semibold">Error</Badge>;
      default:
        return <Badge variant="outline" className="text-xs rounded-full px-3 py-1 font-semibold">Unknown</Badge>;
    }
  };

  // Show connection prompt if not connected to Google Search Console
  if (!loading && !isConnectedToGoogle) {
    return (
      <div className="space-y-8">
        {/* Blurred content preview */}
        <div className="relative">
          <div className="opacity-30 blur-sm pointer-events-none">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <Card key={i} className="bg-background/95 backdrop-blur-md border border-border/40 rounded-3xl">
                  <CardHeader className="pb-4 pt-6 px-6">
                    <div className="h-4 w-24 bg-muted rounded"></div>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    <div className="h-9 w-16 bg-muted rounded mb-2"></div>
                    <div className="h-4 w-32 bg-muted rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Table preview */}
            <Card className="bg-background/95 backdrop-blur-md border border-border/40 rounded-3xl">
              <CardHeader>
                <div className="h-6 w-48 bg-muted rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-16 bg-muted/50 rounded-lg"></div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Premium blur overlay - matching dashboard overview style */}
          <div className="absolute inset-0 backdrop-blur-md bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl flex items-center justify-center">
            <div className="bg-card rounded-3xl border border-primary/20 px-8 py-8 text-center shadow-2xl max-w-lg w-[95%] mx-auto"
                 style={{
                   boxShadow: '0 35px 80px -45px rgba(59, 130, 246, 0.2), 0 20px 40px -20px rgba(59, 130, 246, 0.15)'
                 }}>
              <div className="text-primary/80 p-4 rounded-2xl bg-primary/10 border border-primary/20 inline-block mb-4">
                <Link2 className="h-8 w-8" />
              </div>
              <div className="text-xl font-bold text-foreground mb-2">
                Connect Google Search Console
              </div>
              <div className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Connect your Google Search Console account to view which pages are indexed by Google, track indexing status, and monitor crawl errors.
              </div>
              <Button
                onClick={connectToGoogle}
                className="rounded-xl px-6 py-3 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <Link2 className="h-4 w-4 mr-2" />
                Connect Google Search Console
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <Skeleton className="h-9 w-48 mb-3" />
            <Skeleton className="h-6 w-96" />
          </div>
          <Skeleton className="h-12 w-40 rounded-full" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card 
              key={i}
              className="
                bg-background/95
                backdrop-blur-md
                border border-border/40
                rounded-3xl
              "
            >
              <CardHeader className="pb-4 pt-6 px-6">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <Skeleton className="h-9 w-16 mb-2" />
                <Skeleton className="h-4 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error && isConnectedToGoogle) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-foreground tracking-tight">Google Index</h2>
            <p className="text-muted-foreground text-lg font-medium mt-2">Monitor your website's indexing status in Google Search</p>
          </div>
          <Button 
            onClick={checkIndexStatus} 
            disabled={checking}
            variant="outline"
            className="gap-2 rounded-full px-6 py-3 text-base font-semibold border-2 border-border/50 bg-background/90 backdrop-blur-sm text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-500 ease-in-out shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Retrying...' : 'Retry'}
          </Button>
        </div>
        <Card className="
          border-2 border-destructive/30
          bg-background/95
          backdrop-blur-lg
          rounded-3xl
          shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
        ">
          <CardContent className="text-center py-12 px-8">
            <div className="p-4 rounded-3xl bg-destructive/10 inline-block mb-4">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">Error Loading Data</h3>
            <p className="text-muted-foreground font-medium mb-6 leading-relaxed">{error}</p>
            <Button 
              onClick={checkIndexStatus} 
              disabled={checking}
              variant="outline"
              className="rounded-full px-6 py-3 text-base font-semibold border-2 border-border/50 bg-background/90 backdrop-blur-sm text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-500 ease-in-out shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checking ? 'Retrying...' : 'Try Again'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-foreground tracking-tight">Google Index</h2>
          <p className="text-muted-foreground text-lg font-medium mt-2">Monitor your website's indexing status in Google Search</p>
        </div>
        <Button 
          onClick={checkIndexStatus} 
          disabled={checking}
          variant="outline"
          className="gap-2 rounded-full px-6 py-3 text-base font-semibold border-2 border-border/50 bg-background/90 backdrop-blur-sm text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-500 ease-in-out shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
          {checking ? 'Checking...' : 'Check Status'}
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="
          bg-background/95
          backdrop-blur-md
          border border-border/40
          shadow-[0_2px_12px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.02)]
          hover:shadow-[0_4px_20px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.03)]
          rounded-3xl
          transition-all duration-300 ease-out
        ">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 pt-6 px-6">
            <CardTitle className="text-sm font-semibold text-muted-foreground tracking-wide">Total Pages</CardTitle>
            <div className="text-primary/80 p-3 rounded-2xl bg-primary/10 border border-primary/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
              <Globe className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <div className="text-3xl font-bold text-foreground mb-2">{summary?.totalPages || 0}</div>
            <p className="text-sm text-muted-foreground font-medium">Found on your site</p>
          </CardContent>
        </Card>

        <Card className="
          bg-background/95
          backdrop-blur-md
          border border-border/40
          shadow-[0_2px_12px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.02)]
          hover:shadow-[0_4px_20px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.03)]
          rounded-3xl
          transition-all duration-300 ease-out
        ">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 pt-6 px-6">
            <CardTitle className="text-sm font-semibold text-muted-foreground tracking-wide">Indexed Pages</CardTitle>
            <div className="text-green-600/80 p-3 rounded-2xl bg-green-500/10 border border-green-500/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
              <CheckCircle className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <div className="text-3xl font-bold text-green-600 mb-2">{summary?.indexedPages || 0}</div>
            <p className="text-sm text-muted-foreground font-medium">
              {summary?.totalPages ? Math.round((summary.indexedPages / summary.totalPages) * 100) : 0}% of total
            </p>
          </CardContent>
        </Card>

        <Card className="
          bg-background/95
          backdrop-blur-md
          border border-border/40
          shadow-[0_2px_12px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.02)]
          hover:shadow-[0_4px_20px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.03)]
          rounded-3xl
          transition-all duration-300 ease-out
        ">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 pt-6 px-6">
            <CardTitle className="text-sm font-semibold text-muted-foreground tracking-wide">Not Indexed</CardTitle>
            <div className="text-red-600/80 p-3 rounded-2xl bg-red-500/10 border border-red-500/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
              <XCircle className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <div className="text-3xl font-bold text-red-600 mb-2">{summary?.notIndexedPages || 0}</div>
            <p className="text-sm text-muted-foreground font-medium">Need attention</p>
          </CardContent>
        </Card>

        <Card className="
          bg-background/95
          backdrop-blur-md
          border border-border/40
          shadow-[0_2px_12px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.02)]
          hover:shadow-[0_4px_20px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.03)]
          rounded-3xl
          transition-all duration-300 ease-out
        ">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 pt-6 px-6">
            <CardTitle className="text-sm font-semibold text-muted-foreground tracking-wide">Crawl Errors</CardTitle>
            <div className="text-yellow-600/80 p-3 rounded-2xl bg-yellow-500/10 border border-yellow-500/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
              <TrendingUp className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <div className="text-3xl font-bold text-yellow-600 mb-2">{summary?.crawlErrors || 0}</div>
            <p className="text-sm text-muted-foreground font-medium">Last 30 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Sitemap Status */}
      <Card className="
        bg-background/95
        backdrop-blur-lg
        border border-border/30
        shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
        hover:shadow-[0_6px_24px_rgba(0,0,0,0.06),0_2px_10px_rgba(0,0,0,0.03)]
        rounded-3xl
        transition-all duration-500 ease-out
      ">
        <CardHeader className="pb-6 pt-8 px-8">
          <CardTitle className="text-2xl font-bold text-foreground tracking-tight">Sitemap Status</CardTitle>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <div className="flex items-center justify-between p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-300">
            <div className="flex items-center gap-4">
              {summary?.sitemapSubmitted ? (
                <div className="p-3 rounded-2xl bg-green-500/10 border border-green-500/20">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                </div>
              ) : (
                <div className="p-3 rounded-2xl bg-red-500/10 border border-red-500/20">
                  <XCircle className="h-6 w-6 text-red-500" />
                </div>
              )}
              <div>
                <p className="font-bold text-foreground text-lg leading-tight">
                  {summary?.sitemapSubmitted ? 'XML Sitemap Submitted' : 'XML Sitemap Not Submitted'}
                </p>
                <p className="text-sm text-muted-foreground font-medium mt-1">
                  {summary?.sitemapSubmitted ? 'Sitemap is active and being processed' : 'Submit your sitemap to Google Search Console'}
                </p>
              </div>
            </div>
            <Badge 
              variant={summary?.sitemapSubmitted ? "default" : "destructive"}
              className="rounded-full px-4 py-1.5 font-semibold text-sm"
            >
              {summary?.sitemapSubmitted ? 'Active' : 'Not Submitted'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Pages Index Status */}
      <Card className="
        bg-background/95
        backdrop-blur-lg
        border border-border/30
        shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
        hover:shadow-[0_6px_24px_rgba(0,0,0,0.06),0_2px_10px_rgba(0,0,0,0.03)]
        rounded-3xl
        transition-all duration-500 ease-out
      ">
        <CardHeader className="pb-6 pt-8 px-8">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-bold text-foreground tracking-tight">Page Index Status</CardTitle>
            {indexData.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground font-medium">Sort by:</span>
                <Select value={sortField} onValueChange={(value: SortField) => handleSortChange(value)}>
                  <SelectTrigger className="w-40 rounded-xl border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="url">URL</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                    <SelectItem value="last_crawled">Last Crawled</SelectItem>
                    <SelectItem value="impressions">Impressions</SelectItem>
                    <SelectItem value="clicks">Clicks</SelectItem>
                    <SelectItem value="avg_position">Position</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSortChange(sortField)}
                  className="px-3 rounded-xl border-2"
                >
                  {getSortIcon(sortField)}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          {indexData.length === 0 ? (
            <div className="text-center py-12">
              <div className="p-4 rounded-3xl bg-muted/20 inline-block mb-4">
                <Search className="h-12 w-12 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">No Data Available</h3>
              <p className="text-muted-foreground font-medium mb-6">
                {isSimulated ? 'Using simulated data. Connect Google Search Console for real data.' : 'No indexing data found for this website.'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {currentPageData.map((page, index) => (
                  <div 
                    key={startIndex + index} 
                    className="flex items-center justify-between p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-300"
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-foreground text-lg leading-tight truncate mb-2">
                          {page.url}
                        </p>
                        <div className="flex items-center gap-4 flex-wrap">
                          <p className="text-sm text-muted-foreground font-medium">
                            Last crawled: {new Date(page.last_crawled).toLocaleDateString()}
                          </p>
                          {page.status === 'indexed' && page.impressions > 0 && (
                            <>
                              <span className="text-xs text-muted-foreground font-medium bg-muted/30 px-2 py-1 rounded-full">
                                {page.impressions} impressions
                              </span>
                              <span className="text-xs text-muted-foreground font-medium bg-muted/30 px-2 py-1 rounded-full">
                                {page.clicks} clicks
                              </span>
                              <span className="text-xs text-muted-foreground font-medium bg-muted/30 px-2 py-1 rounded-full">
                                Position: {page.avg_position}
                              </span>
                            </>
                          )}
                        </div>
                        {page.status === 'error' && (
                          <p className="text-xs text-red-500 font-medium mt-2">Crawl error detected</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {getStatusBadge(page.status)}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex flex-col gap-4 mt-8">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className="rounded-full border-2"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => goToPage(pageNum)}
                              className="w-10 h-10 p-0 rounded-full border-2"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className="rounded-full border-2"
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="text-sm text-muted-foreground font-medium">
                      Showing {startIndex + 1}-{Math.min(endIndex, sortedData.length)} of {sortedData.length} pages
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Data Source Notice */}
      {isSimulated && (
        <Card className="
          border-dashed border-2 border-primary/30
          bg-background/95
          backdrop-blur-lg
          rounded-3xl
          shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
        ">
          <CardContent className="text-center py-12 px-8">
            <div className="p-4 rounded-3xl bg-primary/10 inline-block mb-4">
              <Search className="h-12 w-12 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">Simulated Data</h3>
            <p className="text-muted-foreground font-medium mb-6 leading-relaxed">
              This data is simulated. Connect your Google Search Console to get real-time indexing data and performance metrics.
            </p>
            <Button 
              variant="outline" 
              onClick={() => window.location.href = '/settings'}
              className="rounded-full px-6 py-3 text-base font-semibold border-2 border-primary/30 text-primary hover:bg-primary/10"
            >
              Connect Google Search Console
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}